import { onScopeDispose, ref, Ref, watch } from 'vue';
import type { TCatalogRowMode } from '@/domain/catalog/catalog-layout';
import type { IShownSection } from '@/domain/catalog/catalog-view';

/** Cards near the viewport (this far above or below) mount. */
const ROOT_MARGIN = '1200px 0px 1200px 0px';
/**
 * The first render mounts cards until they cover this many viewports (plan
 * §5.8 budget fix 1: the observer mounts the next ones right after).
 */
const EAGER_VIEWPORTS = 1;

const HEADER_HEIGHT = 68;
const FOOTER_HEIGHT = 56;
const WIDE_ROW_HEIGHT = 76;
const COMPACT_ROW_HEIGHT = 96;

export interface ICatalogWindowingDeps {
  sections: Ref<readonly IShownSection[]>;
  isCollapsed: (categoryId: number) => boolean;
  rowMode: Ref<TCatalogRowMode>;
  viewHeight: Ref<number>;
  scroller: Ref<HTMLElement | null>;
}

/**
 * Category-level windowing of the Menú page (plan §5.8): cards mount eagerly
 * down to one viewport, the rest render a placeholder of their estimated
 * height until an IntersectionObserver sees them near. Mounted cards stay
 * mounted (stable scroll-spy offsets, a focused field is never unmounted)
 * until `reset` (menu switch, filter or search change).
 */
export function useCatalogWindowing(deps: ICatalogWindowingDeps) {
  const mounted = ref<Record<number, true>>({});
  const observed = new Map<Element, number>();
  let observer: IntersectionObserver | null = null;

  function estimate(section: IShownSection): number {
    if (deps.isCollapsed(section.category.id)) return HEADER_HEIGHT;
    const row =
      deps.rowMode.value === 'wide' ? WIDE_ROW_HEIGHT : COMPACT_ROW_HEIGHT;
    return HEADER_HEIGHT + section.items.length * row + FOOTER_HEIGHT;
  }

  function eagerWindow(): Record<number, true> {
    const budget = Math.max(deps.viewHeight.value, 600) * EAGER_VIEWPORTS;
    const eager: Record<number, true> = {};
    let height = 0;
    for (const section of deps.sections.value) {
      if (height >= budget) break;
      eager[section.category.id] = true;
      height += estimate(section);
    }
    return eager;
  }

  /** The eager window, plus the card that holds the focused field. */
  function reset() {
    const next = eagerWindow();
    const focused = document.activeElement
      ?.closest?.('[data-category-id]')
      ?.getAttribute('data-category-id');
    if (focused) next[Number(focused)] = true;
    mounted.value = next;
  }

  function mount(categoryId: number) {
    if (!mounted.value[categoryId])
      mounted.value = { ...mounted.value, [categoryId]: true };
  }

  function ensureObserver(): IntersectionObserver | null {
    if (observer) return observer;
    const root = deps.scroller.value;
    if (!root || typeof IntersectionObserver === 'undefined') return null;
    observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => observed.get(entry.target))
          .filter((id): id is number => id !== undefined);
        if (!visible.length) return;
        const next = { ...mounted.value };
        for (const id of visible) next[id] = true;
        mounted.value = next;
      },
      { root, rootMargin: ROOT_MARGIN, threshold: 0 }
    );
    return observer;
  }

  /** A placeholder asks to be mounted when it comes near. */
  function observe(element: Element, categoryId: number) {
    const io = ensureObserver();
    if (!io) {
      mount(categoryId);
      return;
    }
    observed.set(element, categoryId);
    io.observe(element);
  }

  function unobserve(element: Element) {
    observed.delete(element);
    observer?.unobserve(element);
  }

  // The scroller can change (the page remounts its canvas).
  watch(deps.scroller, () => {
    observer?.disconnect();
    observer = null;
    const elements = [...observed.entries()];
    observed.clear();
    for (const [element, id] of elements) observe(element, id);
  });

  onScopeDispose(() => {
    observer?.disconnect();
    observer = null;
    observed.clear();
  });

  return {
    mounted,
    isMounted: (categoryId: number) => !!mounted.value[categoryId],
    estimate,
    reset,
    mount,
    observe,
    unobserve,
  };
}

export type ICatalogWindowing = ReturnType<typeof useCatalogWindowing>;
